/**
 * Scheduler utility for calculating next episode dates
 */

/**
 * 次回放送日を計算
 * @param {Object} schedule 放送スケジュール情報
 * @returns {Date|null} 次回放送日
 */
export function calculateNextEpisodeDate(schedule) {
  if (!schedule || schedule.dayOfWeek === undefined) {
    return null
  }

  const now = new Date()
  const currentDay = now.getDay()
  const currentHour = now.getHours()
  const currentMinute = now.getMinutes()

  // 放送曜日までの日数を計算
  let daysUntilBroadcast = schedule.dayOfWeek - currentDay

  // 今日が放送日の場合
  if (daysUntilBroadcast === 0) {
    // 既に放送時間を過ぎている場合は来週
    const broadcastMinutes = schedule.hour * 60 + schedule.minute
    const currentMinutes = currentHour * 60 + currentMinute

    if (currentMinutes >= broadcastMinutes) {
      daysUntilBroadcast = 7
    }
  }

  // 過去の曜日の場合は来週
  if (daysUntilBroadcast < 0) {
    daysUntilBroadcast += 7
  }

  // 次回放送日を計算
  const nextDate = new Date(now)
  nextDate.setDate(now.getDate() + daysUntilBroadcast)
  nextDate.setHours(schedule.hour, schedule.minute, 0, 0)

  return nextDate
}

/**
 * 放送中かどうかをチェック
 * @param {Date} nextEpisodeDate 次回放送日
 * @param {number} windowMinutes 放送中とみなす時間窓（分）
 * @returns {boolean}
 */
export function isAiringNow(nextEpisodeDate, windowMinutes = 30) {
  if (!nextEpisodeDate) return false

  const now = new Date()
  const diff = nextEpisodeDate.getTime() - now.getTime()
  const diffMinutes = diff / (1000 * 60)

  // 放送時間の前後30分以内
  return diffMinutes >= -windowMinutes && diffMinutes <= windowMinutes
}

/**
 * 次回放送までの表示テキストを生成
 * @param {Date} nextEpisodeDate 次回放送日
 * @returns {string} 表示テキスト
 */
export function formatNextEpisode(nextEpisodeDate) {
  if (!nextEpisodeDate) {
    return null
  }

  const now = new Date()
  const diff = nextEpisodeDate.getTime() - now.getTime()

  // 過去の場合
  if (diff < 0) {
    return null
  }

  const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

  // 24時間以内
  if (diffDays === 0) {
    if (diffHours === 0) {
      return '間もなく'
    }
    return `${diffHours}時間後`
  }

  // 7日以内
  if (diffDays < 7) {
    const dayNames = ['日', '月', '火', '水', '木', '金', '土']
    const dayName = dayNames[nextEpisodeDate.getDay()]
    const hour = nextEpisodeDate.getHours().toString().padStart(2, '0')
    const minute = nextEpisodeDate.getMinutes().toString().padStart(2, '0')

    return `${dayName} ${hour}:${minute}`
  }

  // 7日以上
  const month = nextEpisodeDate.getMonth() + 1
  const day = nextEpisodeDate.getDate()

  return `${month}/${day}`
}

/**
 * 放送状況のステータスを判定
 * @param {Object} animeEntry アニメエントリ
 * @param {Object} metadata メタデータ
 * @returns {string} ステータス
 */
export function determineAiringStatus(animeEntry, metadata) {
  if (!metadata) {
    return 'unknown'
  }

  // MALのステータスを使用
  if (metadata.status) {
    switch (metadata.status.toLowerCase()) {
      case 'currently airing':
        return 'currently_airing'
      case 'finished airing':
        return 'finished'
      case 'not yet aired':
        return 'not_yet_aired'
      default:
        return 'unknown'
    }
  }

  // airingフラグを使用
  if (metadata.airing === true) {
    return 'currently_airing'
  }
  if (metadata.airing === false) {
    return 'finished'
  }

  return 'unknown'
}

/**
 * 視聴進捗をパーセンテージで計算
 * @param {number} current 現在のエピソード
 * @param {number} total 総エピソード数
 * @returns {number} 進捗率（0-100）
 */
export function calculateProgress(current, total) {
  if (!total || total <= 0) {
    return 0
  }

  const progress = (current / total) * 100
  return Math.min(100, Math.max(0, progress))
}

export default {
  calculateNextEpisodeDate,
  isAiringNow,
  formatNextEpisode,
  determineAiringStatus,
  calculateProgress
}
